"use client";

import { useState, useEffect } from "react";
import { FormErrors, FormData, CONTACT_INFO, SUBJECTS } from "@/lib/data";

const API_BASE = process.env.NEXT_PUBLIC_API_URL || "/api";

export default function Contact() {
  const [visible, setVisible] = useState(false);
  const [form, setForm] = useState<FormData>({
    name: "", email: "", phone: "", subject: "", message: "",
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState<string>("");

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  // ── Per-field validators ──────────────────────────────────────────────────
  const validators: Record<string, (val: string) => string | undefined> = {
    name: (v) => {
      if (!v.trim()) return "Name is required";
      if (!/^[A-Za-z ]+$/.test(v)) return "Only letters and spaces are allowed";
      if (v.trim().length < 2) return "Name must be at least 2 characters";
      if (v.trim().length > 50) return "Name must be under 50 characters";
    },
    email: (v) => {
      if (!v.trim()) return "Email is required";
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return "Enter a valid email address";
      if (v.length > 100) return "Email must be under 100 characters";
    },
    phone: (v) => {
      if (!v) return undefined; // optional
      if (!/^[6-9][0-9]{9}$/.test(v)) return "Enter a valid 10-digit mobile number starting with 6–9";
    },
    subject: (v) => {
      if (!v) return "Please select a subject";
    },
    message: (v) => {
      if (!v.trim()) return "Message is required";
      if (v.trim().length < 10) return "Message must be at least 10 characters";
      if (v.trim().length > 500) return "Message must be under 500 characters";
    },
  };

  const validateField = (name: string, value: string): string | undefined =>
    validators[name]?.(value);

  const validate = (): boolean => {
    const e: FormErrors = {};
   (Object.keys(validators) as Array<keyof FormErrors>).forEach((field) => {
      const msg = validateField(field as string, form[field as keyof FormData] ?? "");
      if (msg) e[field] = msg;
    });
    setErrors(e);
    // Mark all as touched so errors show
    setTouched(Object.fromEntries(Object.keys(validators).map((k) => [k, true])));
    return Object.keys(e).length === 0;
  };

  // ── Sanitise on change: block forbidden chars per field ───────────────────
  const sanitise: Record<string, (raw: string) => string> = {
    name:    (v) => v.replace(/[^A-Za-z ]/g, ""),   
    phone:   (v) => v.replace(/\D/g, "").slice(0, 10), 
    email:   (v) => v,                               
    subject: (v) => v,
    message: (v) => v,
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    const cleaned = sanitise[name] ? sanitise[name](value) : value;
    setForm((p) => ({ ...p, [name]: cleaned }));

    // Validate inline only after the field has been touched
    if (touched[name]) {
      const msg = validateField(name, cleaned);
      setErrors((p) => ({ ...p, [name]: msg }));
    }
  };

  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setTouched((p) => ({ ...p, [name]: true }));
    const msg = validateField(name, value);
    setErrors((p) => ({ ...p, [name]: msg }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setStatus("sending");
    setErrorMsg("");

    try {
      const res = await fetch(`${API_BASE}/Messages-form/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          full_name: form.name.trim(),
          email:     form.email.trim(),
          phone:     form.phone.trim() || null,
          subject:   form.subject.trim() || null,
          message:   form.message.trim(),
        }),
      });

      const json = await res.json();

      if (json.success) {
        setStatus("sent");
        setForm({ name: "", email: "", phone: "", subject: "", message: "" });
        setTouched({});
        setErrors({});
      } else {
        setStatus("error");
        setErrorMsg(json.message || "Something went wrong. Please try again.");
      }
    } catch {
      setStatus("error");
      setErrorMsg("Network error. Please check your connection and try again.");
    }
  };

  // ── Shared style helpers ──────────────────────────────────────────────────
  const inputBase = `w-full bg-[#faf8f5] border px-5 py-4 font-['DM_Mono'] text-[13px] text-[#1a1a1a] tracking-[0.04em] placeholder:text-[#aaa] outline-none transition-all duration-200 focus:bg-white`;

  const fieldClass = (name: string) => {
    const hasError = touched[name] && errors[name as keyof FormErrors];
    const isValid  = touched[name] && !errors[name as keyof FormErrors] && form[name as keyof FormData];
    return [
      inputBase,
      hasError ? "border-rose-400 bg-rose-50/40 focus:border-rose-500"
               : isValid  ? "border-emerald-400 focus:border-emerald-500"
                          : "border-[#e8e3dc] focus:border-[#b8955a]",
    ].join(" ");
  };

  // Character counter for message
  const msgLen = form.message.trim().length;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&family=DM+Mono:wght@300;400&display=swap');
        .custom-select { -webkit-appearance: none; appearance: none; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' fill='none'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%23b8955a' stroke-width='1.2' stroke-linecap='round'/%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 18px center; }
      `}</style>

      <main className="bg-[#fdfbf8] text-[#1a1a1a] font-['Cormorant_Garamond',serif] min-h-screen pt-20">

        {/* ── HERO ── */}
        <section className="relative w-full h-[60vh] flex items-center justify-center overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1423666639041-f56000c27a9a?q=80&w=2070&auto=format&fit=crop"
            alt="Contact Hero"
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-black/30" />
          <div className="relative z-10 text-center text-white px-6">
            <h1 className="font-['Cormorant_Garamond'] italic text-6xl md:text-8xl font-light">
              Get in touch
            </h1>
          </div>
        </section>

        {/* ── HEADER INTRO ── */}
        <section className="py-12 px-6 md:px-14 max-w-[1200px] mx-auto">
          <div className={`transition-all duration-1000 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <p className="font-['DM_Mono'] text-[11px] tracking-[0.3em] uppercase text-[#051173] mb-6">
              Let's connect
            </p>
            <h1 className="italic font-light text-[clamp(2.5rem,5vw,2rem)] mb-8">
              We'd love to hear about your{" "}
              <span className="text-[#b8955a]">Dream Silhouette</span>
            </h1>
          </div>
        </section>

        {/* ── INFO STRIP ── */}
        <section className="border-y border-[#ede8e0] bg-[#faf8f5]">
          <div className="max-w-[1200px] mx-auto grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-[#ede8e0]">
            {CONTACT_INFO.map((info) => (
              <div
                key={info.id}
                className="px-10 py-12 flex flex-col items-center text-center gap-6 hover:bg-white transition-colors"
              >
                <span className="text-[32px] text-[#b8955a]">{info.icon}</span>
                <div>
                  <p className="font-['DM_Mono'] text-[11px] tracking-[0.25em] uppercase text-[#051173] mb-4">
                    {info.label}
                  </p>
                  <div className="space-y-1">
                    {info.lines.map((line, i) => (
                      <p
                        key={i}
                        className={`font-['DM_Mono'] text-[14px] leading-[1.6] ${i === 0 ? "text-[#1a1a1a] font-medium" : "text-[black]"}`}
                      >
                        {line}
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── FORM + MAP ── */}
        <section className="py-12 px-6 md:px-14 max-w-[1200px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-20">

          {/* ── FORM ── */}
          <div>
            <h2 className="italic font-light text-[2.8rem] mb-10">Feedback Form</h2>

            {status === "sent" ? (
              <div className="border border-[#b8955a] p-10 text-center bg-[#faf8f5]">
                <div className="text-4xl mb-4">✉️</div>
                <h3 className="text-2xl italic mb-4">Thank You!</h3>
                <p className="font-['DM_Mono'] text-sm text-gray-500 mb-6">
                  Your message has been received. We'll get back to you soon.
                </p>
                <button
                  type="button"
                  onClick={() => setStatus("idle")}
                  className="border border-black px-8 py-3 uppercase text-[10px] tracking-widest hover:bg-black hover:text-white transition-all"
                >
                  Send Another Message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-5">

                {/* Global error banner */}
                {status === "error" && errorMsg && (
                  <div className="bg-rose-50 border border-rose-200 text-rose-600 font-['DM_Mono'] text-[11px] px-4 py-3 flex justify-between items-start gap-3">
                    <span>⚠️ {errorMsg}</span>
                    <button
                      type="button"
                      onClick={() => { setStatus("idle"); setErrorMsg(""); }}
                      className="shrink-0 text-rose-400 hover:text-rose-600 font-bold"
                    >✕</button>
                  </div>
                )}

                {/* Name + Email */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="flex flex-col gap-1">
                    <div className="relative">
                      <input
                        type="text"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="Your name *"
                        className={fieldClass("name")}
                      />
                      {/* Inline tick / cross icon */}
                      {touched.name && (
                        <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-sm ${errors.name ? "text-rose-400" : "text-emerald-500"}`}>
                          {errors.name ? "✕" : "✓"}
                        </span>
                      )}
                    </div>
                    {touched.name && errors.name && (
                      <p className="font-['DM_Mono'] text-[10px] text-rose-500 tracking-wide">
                        ⚠ {errors.name}
                      </p>
                    )}
                  </div>

                  <div className="flex flex-col gap-1">
                    <div className="relative">
                      <input
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="Email address *"
                        className={fieldClass("email")}
                      />
                      {touched.email && (
                        <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-sm ${errors.email ? "text-rose-400" : "text-emerald-500"}`}>
                          {errors.email ? "✕" : "✓"}
                        </span>
                      )}
                    </div>
                    {touched.email && errors.email && (
                      <p className="font-['DM_Mono'] text-[10px] text-rose-500 tracking-wide">
                        ⚠ {errors.email}
                      </p>
                    )}
                  </div>
                </div>

                {/* Phone + Subject */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div className="flex flex-col gap-1">
                      <div className="relative">
                        <input
                          type="tel"
                          name="phone"
                          value={form.phone}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder="Phone (optional)"
                          inputMode="numeric"
                          className={fieldClass("phone")}
                        />
                        {touched.phone && !!form.phone && (
                          <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-sm ${!!errors.phone ? "text-rose-400" : "text-emerald-500"}`}>
                            {!!errors.phone ? "✕" : "✓"}
                          </span>
                        )}
                      </div>  {/* ← this </div> closes "relative", was missing */}
                      {touched.phone && !!errors.phone && (
                        <p className="font-['DM_Mono'] text-[10px] text-rose-500 tracking-wide">
                          ⚠ {errors.phone}
                        </p>
                      )}
                    </div>  {/* ← closes "flex flex-col gap-1" */}

                    <div className="flex flex-col gap-1">
                      <select
                        name="subject"
                        value={form.subject}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={`${fieldClass("subject")} custom-select`}
                      >
                        <option value="">Select subject *</option>
                        {SUBJECTS.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                      {touched.subject && !!errors.subject && (
                        <p className="font-['DM_Mono'] text-[10px] text-rose-500 tracking-wide">
                          ⚠ {errors.subject}
                        </p>
                      )}
                    </div>
                  </div>

                {/* Message */}
                <div className="flex flex-col gap-1">
                  <div className="relative">
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Your message *"
                      rows={6}
                      className={`${fieldClass("message")} resize-none`}
                    />
                  </div>
                  <div className="flex justify-between items-center">
                    {touched.message && errors.message ? (
                      <p className="font-['DM_Mono'] text-[10px] text-rose-500 tracking-wide">
                        ⚠ {errors.message}
                      </p>
                    ) : <span />}
                    {/* Character counter */}
                    <p className={`font-['DM_Mono'] text-[10px] tracking-wide ml-auto ${msgLen > 480 ? "text-rose-400" : "text-[#aaa]"}`}>
                      {msgLen}/500
                    </p>
                  </div>
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={status === "sending"}
                  className="bg-[#1a1a1a] text-white py-5 px-10 uppercase text-[11px] tracking-[0.2em] hover:bg-[#b8955a] transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-3"
                >
                  {status === "sending" ? (
                    <>
                      <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Sending...
                    </>
                  ) : "Send Message →"}
                </button>

              </form>
            )}
          </div>

          {/* ── MAP ── */}
          <div className="flex flex-col gap-8">
            <h2 className="italic font-light text-[2.8rem] text-[#2a2a2a]">Find us</h2>
            <div className="w-full h-[450px] overflow-hidden rounded-3xl border border-gray-200 shadow-xl">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3200.2065171402714!2d77.43909712255147!3d28.579765427412877!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390cef6a4a06bd8f%3A0x4c227f795de288b4!2sModish%20Kreations!5e0!3m2!1sen!2sin!4v1776755047554!5m2!1sen!2sin"
                width="600" height="450" style={{ border: 0 }}
                allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          {/* ── STORE TIMING ── */}
          <div className="bg-[#F8F0E3] p-8 border border-black rounded-3xl">
            <h4 className="text-[10px] uppercase tracking-[0.25em] font-black text-black mb-6">Timings</h4>
            <div className="space-y-4 font-['DM_Mono'] text-[13px] text-black">
              <div className="flex justify-between items-center border-b border-black/10 pb-3">
                <span className="font-bold">Wed - Mon</span>
                <span className="text-right">10:00 am – 8:00 pm</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="font-bold">Tuesday</span>
                <span className="text-red-600 font-bold uppercase tracking-widest">Closed</span>
              </div>
            </div>
          </div>

        </section>
      </main>
    </>
  );
}